import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { onboardingService } from '../services/onboardingService';
import '../styles/FormBuilder.css';

/* ── Model ───────────────────────────────────────────────── */
type FieldType =
  | 'headline' | 'description' | 'image'
  | 'short-text' | 'numeric' | 'email' | 'long-text'
  | 'dropdown' | 'radio' | 'multi-select' | 'date' | 'file'
  | 'repeating-group';

interface FormField {
  id: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  required?: boolean;
  options?: string[];           // dropdown / radio / multi-select
  children?: FormField[];       // repeating-group
  imageUrl?: string;            // image content block (data URL)
}

interface FormDoc {
  id: string;
  name: string;
  headline: string;
  fields: FormField[];
}

const uid = () => Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-3);

const FIELD_META: Record<FieldType, { label: string; group: 'content' | 'input' | 'grouping'; defaultLabel: string; hasOptions?: boolean }> = {
  'headline':        { label: 'Headline',           group: 'content',  defaultLabel: 'Headline' },
  'description':     { label: 'Description',         group: 'content',  defaultLabel: 'Description text' },
  'image':           { label: 'Image',              group: 'content',  defaultLabel: 'Image' },
  'short-text':      { label: 'Short text',          group: 'input',    defaultLabel: 'Short text' },
  'numeric':         { label: 'Numeric Values',      group: 'input',    defaultLabel: 'Number' },
  'email':           { label: 'Email Address',       group: 'input',    defaultLabel: 'Email address' },
  'long-text':       { label: 'Long text',           group: 'input',    defaultLabel: 'Long text' },
  'dropdown':        { label: 'Dropdown',            group: 'input',    defaultLabel: 'Dropdown', hasOptions: true },
  'radio':           { label: 'Radio buttons',       group: 'input',    defaultLabel: 'Choose one', hasOptions: true },
  'multi-select':    { label: 'Multi select',        group: 'input',    defaultLabel: 'Select multiple', hasOptions: true },
  'date':            { label: 'Date',                group: 'input',    defaultLabel: 'Date' },
  'file':            { label: 'File Uploads',        group: 'input',    defaultLabel: 'Upload a file' },
  'repeating-group': { label: 'Repeating Field Group', group: 'grouping', defaultLabel: 'Repeating group' },
};

/* ── Icons (match the field menu in the reference) ───────── */
const TypeIcon: React.FC<{ type: FieldType }> = ({ type }) => {
  const common = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (type) {
    case 'headline': return <svg {...common}><line x1="4" y1="7" x2="20" y2="7"/><line x1="4" y1="12" x2="14" y2="12"/></svg>;
    case 'description': return <svg {...common}><line x1="4" y1="8" x2="20" y2="8"/><line x1="4" y1="13" x2="20" y2="13"/><line x1="4" y1="18" x2="14" y2="18"/></svg>;
    case 'image': return <svg {...common}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>;
    case 'short-text': return <svg {...common}><rect x="3" y="8" width="18" height="8" rx="1.5"/><line x1="7" y1="12" x2="7" y2="12"/></svg>;
    case 'numeric': return <svg {...common}><path d="M4 9h16M4 15h16M10 4l-2 16M16 4l-2 16"/></svg>;
    case 'email': return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></svg>;
    case 'long-text': return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M7 16l3-3"/></svg>;
    case 'dropdown': return <svg {...common}><polyline points="6 9 12 15 18 9"/></svg>;
    case 'radio': return <svg {...common}><circle cx="6" cy="7" r="2"/><line x1="11" y1="7" x2="20" y2="7"/><circle cx="6" cy="17" r="2"/><line x1="11" y1="17" x2="20" y2="17"/></svg>;
    case 'multi-select': return <svg {...common}><line x1="9" y1="6" x2="20" y2="6"/><line x1="9" y1="12" x2="20" y2="12"/><line x1="9" y1="18" x2="20" y2="18"/><line x1="4" y1="6" x2="5" y2="6"/><line x1="4" y1="12" x2="5" y2="12"/><line x1="4" y1="18" x2="5" y2="18"/></svg>;
    case 'date': return <svg {...common}><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M3 10h18M8 2v4M16 2v4"/><polyline points="9 15 11 17 15 13"/></svg>;
    case 'file': return <svg {...common}><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M12 16V9M9 12l3-3 3 3"/></svg>;
    case 'repeating-group': return <svg {...common}><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M7 7V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2"/></svg>;
  }
};

/* ── Field-type picker menu (the popover) ────────────────── */
const FieldMenu: React.FC<{ onPick: (t: FieldType) => void; onClose: () => void }> = ({ onPick, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);
  const groups: { title: string; items: FieldType[] }[] = [
    { title: 'Content', items: ['headline', 'description', 'image'] },
    { title: 'Input', items: ['short-text', 'numeric', 'email', 'long-text', 'dropdown', 'radio', 'multi-select', 'date', 'file'] },
    { title: 'Grouping', items: ['repeating-group'] },
  ];
  return (
    <div className="fb-menu" ref={ref}>
      {groups.map(g => (
        <div key={g.title} className="fb-menu-group">
          <div className="fb-menu-title">{g.title}</div>
          {g.items.map(t => (
            <button key={t} type="button" className="fb-menu-item" onClick={() => { onPick(t); onClose(); }}>
              <span className="fb-menu-icon"><TypeIcon type={t} /></span>
              <span>{FIELD_META[t].label}</span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
};

/* ── Option editor (dropdown / radio / multi-select) ─────── */
const OptionsEditor: React.FC<{ options: string[]; onChange: (o: string[]) => void }> = ({ options, onChange }) => (
  <div className="fb-options">
    {options.map((opt, i) => (
      <div key={i} className="fb-option-row">
        <span className="fb-option-bullet" />
        <input className="fb-option-input" value={opt} placeholder={`Option ${i + 1}`} onChange={e => onChange(options.map((o, j) => j === i ? e.target.value : o))} />
        <button type="button" className="fb-option-remove" title="Remove option" onClick={() => onChange(options.filter((_, j) => j !== i))} disabled={options.length <= 1}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
    ))}
    <button type="button" className="fb-option-add" onClick={() => onChange([...options, ''])}>+ Add option</button>
  </div>
);

/* ── A single editable field card ────────────────────────── */
const FieldCard: React.FC<{
  field: FormField;
  onChange: (f: FormField) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  canUp: boolean;
  canDown: boolean;
}> = ({ field, onChange, onRemove, onMove, canUp, canDown }) => {
  const meta = FIELD_META[field.type];
  const set = (patch: Partial<FormField>) => onChange({ ...field, ...patch });
  const isContent = field.type === 'headline' || field.type === 'description' || field.type === 'image';

  return (
    <div className="fb-card">
      <div className="fb-card-head">
        <span className="fb-card-type"><TypeIcon type={field.type} /> {meta.label}</span>
        <div className="fb-card-actions">
          <button type="button" className="fb-icon-btn" disabled={!canUp} title="Move up" onClick={() => onMove(-1)}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg></button>
          <button type="button" className="fb-icon-btn" disabled={!canDown} title="Move down" onClick={() => onMove(1)}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg></button>
          <button type="button" className="fb-icon-btn fb-icon-danger" title="Delete" onClick={onRemove}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></button>
        </div>
      </div>

      {/* Label / text */}
      {field.type === 'headline' ? (
        <input className="fb-headline-input" value={field.label} placeholder="Section headline" onChange={e => set({ label: e.target.value })} />
      ) : field.type === 'description' ? (
        <textarea className="fb-desc-input" rows={2} value={field.label} placeholder="Description text" onChange={e => set({ label: e.target.value })} />
      ) : field.type === 'image' ? (
        <ImageUploadField url={field.imageUrl} onChange={url => set({ imageUrl: url })} />
      ) : (
        <input className="fb-label-input" value={field.label} placeholder="Field label" onChange={e => set({ label: e.target.value })} />
      )}

      {/* Type-specific config */}
      {meta.hasOptions && (
        <OptionsEditor options={field.options ?? ['']} onChange={o => set({ options: o })} />
      )}

      {field.type === 'repeating-group' && (
        <RepeatingGroupEditor field={field} onChange={onChange} />
      )}

      {/* Footer: required toggle (not for content) */}
      {!isContent && (
        <label className="fb-required">
          <input type="checkbox" checked={!!field.required} onChange={e => set({ required: e.target.checked })} />
          Required
        </label>
      )}
    </div>
  );
};

/* ── Repeating group — holds nested fields ───────────────── */
const RepeatingGroupEditor: React.FC<{ field: FormField; onChange: (f: FormField) => void }> = ({ field, onChange }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const children = field.children ?? [];
  const setChildren = (next: FormField[]) => onChange({ ...field, children: next });
  const addChild = (t: FieldType) => setChildren([...children, makeField(t)]);
  return (
    <div className="fb-group">
      {children.map((c, i) => (
        <FieldCard
          key={c.id}
          field={c}
          onChange={f => setChildren(children.map((x, j) => j === i ? f : x))}
          onRemove={() => setChildren(children.filter((_, j) => j !== i))}
          onMove={dir => {
            const j = i + dir; if (j < 0 || j >= children.length) return;
            const next = [...children]; [next[i], next[j]] = [next[j], next[i]]; setChildren(next);
          }}
          canUp={i > 0}
          canDown={i < children.length - 1}
        />
      ))}
      <div className="fb-group-add">
        <button type="button" className="fb-add-field" onClick={() => setMenuOpen(o => !o)}>
          <span className="fb-plus">+</span> Add field to group
        </button>
        {menuOpen && <FieldMenu onPick={t => { addChild(t); }} onClose={() => setMenuOpen(false)} />}
      </div>
    </div>
  );
};

/* Image content block — upload an image (stored as a data URL). */
const ImageUploadField: React.FC<{ url?: string; onChange: (url: string) => void }> = ({ url, onChange }) => {
  const ref = useRef<HTMLInputElement>(null);
  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) { if (file) alert('Please choose an image file.'); return; }
    const r = new FileReader();
    r.onload = () => onChange(r.result as string);
    r.readAsDataURL(file);
  };
  return (
    <div className="fb-image-field">
      <input ref={ref} type="file" accept="image/*" style={{ display: 'none' }} onChange={pick} />
      {url ? (
        <div className="fb-image-preview">
          <img src={url} alt="uploaded" />
          <button type="button" className="fb-image-replace" onClick={() => ref.current?.click()}>Replace image</button>
        </div>
      ) : (
        <button type="button" className="fb-image-upload" onClick={() => ref.current?.click()}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
          Upload an image
        </button>
      )}
    </div>
  );
};

function makeField(type: FieldType): FormField {
  const meta = FIELD_META[type];
  return {
    id: uid(),
    type,
    label: meta.defaultLabel,
    required: false,
    ...(meta.hasOptions ? { options: ['Option 1', 'Option 2'] } : {}),
    ...(type === 'repeating-group' ? { children: [] } : {}),
  };
}

/* ── Live preview renderer (professional, Google-Forms-like) ── */
const FileDrop: React.FC = () => (
  <label className="fbp-filedrop">
    <input type="file" hidden />
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
    <span className="fbp-filedrop-main">Click to upload</span>
    <span className="fbp-filedrop-sub">or drag and drop a file</span>
  </label>
);

const PreviewField: React.FC<{ field: FormField; inGroup?: boolean }> = ({ field, inGroup }) => {
  if (field.type === 'headline') return <h3 className="fbp-headline">{field.label || 'Headline'}</h3>;
  if (field.type === 'description') return <p className="fbp-desc">{field.label}</p>;
  if (field.type === 'image') return field.imageUrl ? <img className="fbp-image" src={field.imageUrl} alt={field.label} /> : null;

  const label = <label className="fbp-label">{field.label || 'Untitled field'}{field.required && <span className="fbp-req">*</span>}</label>;
  const body = (() => {
    switch (field.type) {
      case 'short-text': return <input className="fbp-input" placeholder={field.placeholder || 'Your answer'} />;
      case 'numeric': return <input type="number" className="fbp-input" placeholder="0" />;
      case 'email': return <input type="email" className="fbp-input" placeholder="name@example.com" />;
      case 'long-text': return <textarea className="fbp-input fbp-textarea" rows={4} placeholder="Your answer" />;
      case 'date': return <input type="date" className="fbp-input fbp-date" />;
      case 'file': return <FileDrop />;
      case 'dropdown':
        return (
          <div className="fbp-dropdown">
            <select className="fbp-input fbp-select"><option value="">Choose an option</option>{(field.options ?? []).map((o, i) => <option key={i}>{o}</option>)}</select>
            <svg className="fbp-select-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        );
      case 'radio':
        return <div className="fbp-choices">{(field.options ?? []).map((o, i) => <label key={i} className="fbp-choice"><span className="fbp-radio" /><span>{o}</span><input type="radio" name={field.id} hidden /></label>)}</div>;
      case 'multi-select':
        return <div className="fbp-choices">{(field.options ?? []).map((o, i) => <label key={i} className="fbp-choice"><span className="fbp-checkbox" /><span>{o}</span><input type="checkbox" hidden /></label>)}</div>;
      case 'repeating-group':
        // In preview, groups are shown inline (no title, no "add another").
        return <div className="fbp-group-box">{(field.children ?? []).map(c => <PreviewField key={c.id} field={c} inGroup />)}</div>;
      default: return null;
    }
  })();

  if (field.type === 'repeating-group') return <div className="fbp-field">{body}</div>;
  return <div className={`fbp-field ${inGroup ? 'fbp-field-group' : ''}`}>{label}{body}</div>;
};

/* ── Page ────────────────────────────────────────────────── */
const FormBuilder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [doc, setDoc] = useState<FormDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [preview, setPreview] = useState(searchParams.get('preview') === '1');
  const [saved, setSaved] = useState(true);

  const [loadError, setLoadError] = useState<string | null>(null);

  // Load an existing form (created via the "Create Form" popup). Direct hits to
  // /new fall back to creating one so the route never dead-ends.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadError(null);
      try {
        if (id && id !== 'new') {
          const f = await onboardingService.getForm(id);
          if (!cancelled) setDoc(f ? { id: f.id, name: f.name, headline: f.headline, fields: f.fields } : null);
        } else {
          const created = await onboardingService.createForm({ name: 'Untitled form', headline: 'Enter a headline', fields: [] });
          if (!cancelled) { setDoc({ id: created.id, name: created.name, headline: created.headline, fields: created.fields }); navigate(`/onboarding/forms/${created.id}/edit`, { replace: true }); }
        }
      } catch (err: any) {
        console.error('Failed to load/create form:', err);
        if (!cancelled) setLoadError(err?.message ?? 'Something went wrong.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [id]);

  if (loading || !doc) {
    return (
      <Layout>
        <div className="fb-page">
          <div className="fb-loading">
            {loading ? 'Loading…' : (loadError ? `Couldn’t open this form: ${loadError}` : 'Form not found.')}
            {!loading && <div style={{ marginTop: 12 }}><button className="fb-back" style={{ width: 'auto', padding: '8px 14px' }} onClick={() => navigate('/onboarding/forms')}>Back to Forms</button></div>}
          </div>
        </div>
      </Layout>
    );
  }

  // Mark dirty on edit.
  const update = (next: FormDoc) => { setDoc(next); setSaved(false); };

  const addField = (t: FieldType) => update({ ...doc, fields: [...doc.fields, makeField(t)] });
  const setFields = (fields: FormField[]) => update({ ...doc, fields });

  const handleSave = async () => {
    try { await onboardingService.updateForm({ id: doc.id, name: doc.name, headline: doc.headline, fields: doc.fields }); setSaved(true); }
    catch (err: any) { console.error('Failed to save form:', err); alert(`Failed to save: ${err?.message ?? err}`); }
  };
  const handleBack = async () => { if (!saved) { await handleSave(); } navigate('/onboarding/forms'); };

  const fieldCount = doc.fields.length;

  return (
    <Layout>
    <div className="fb-page fb-page-inline">
      {/* Header */}
      <div className="fb-header">
        <div className="fb-header-left">
          <button type="button" className="fb-back" onClick={handleBack} title="Back to forms">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <input className="fb-form-name" value={doc.name} onChange={e => update({ ...doc, name: e.target.value })} placeholder="Form name" />
        </div>
        <div className="fb-header-actions">
          <button type="button" className="fb-preview" onClick={() => setPreview(p => !p)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/><circle cx="12" cy="12" r="3"/></svg>
            {preview ? 'Edit form' : 'Preview form'}
          </button>
          <button type="button" className={`fb-save ${saved ? 'saved' : ''}`} onClick={handleSave}>
            {saved ? 'Saved' : 'Save'}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="fb-body">
        {preview ? (
          <div className="fb-preview-canvas">
            <h2 className="fbp-form-title">{doc.name}</h2>
            <h3 className="fbp-headline">{doc.headline}</h3>
            {doc.fields.length === 0 ? <p className="fbp-empty">This form has no fields yet.</p> : doc.fields.map(f => <PreviewField key={f.id} field={f} />)}
            <button type="button" className="fbp-submit">Submit</button>
          </div>
        ) : (
          <div className="fb-canvas">
            {/* Headline row */}
            <div className="fb-headline-row">
              <input className="fb-section-headline" value={doc.headline} onChange={e => update({ ...doc, headline: e.target.value })} placeholder="Enter a headline" />
              <div className="fb-headline-tools">
                <span className="fb-drag" title="Drag"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="6" r="1.4"/><circle cx="15" cy="6" r="1.4"/><circle cx="9" cy="12" r="1.4"/><circle cx="15" cy="12" r="1.4"/><circle cx="9" cy="18" r="1.4"/><circle cx="15" cy="18" r="1.4"/></svg></span>
                <span className="fb-gear" title="Settings"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg></span>
              </div>
            </div>

            {/* Existing fields */}
            <div className="fb-fields">
              {doc.fields.map((f, i) => (
                <FieldCard
                  key={f.id}
                  field={f}
                  onChange={nf => setFields(doc.fields.map((x, j) => j === i ? nf : x))}
                  onRemove={() => setFields(doc.fields.filter((_, j) => j !== i))}
                  onMove={dir => { const j = i + dir; if (j < 0 || j >= doc.fields.length) return; const next = [...doc.fields]; [next[i], next[j]] = [next[j], next[i]]; setFields(next); }}
                  canUp={i > 0}
                  canDown={i < doc.fields.length - 1}
                />
              ))}
            </div>

            {/* Add new field + menu */}
            <div className="fb-add-wrap">
              <button type="button" className="fb-add-field" onClick={() => setMenuOpen(o => !o)}>
                <span className="fb-plus">+</span> Add new field
              </button>
              {menuOpen && <FieldMenu onPick={addField} onClose={() => setMenuOpen(false)} />}
            </div>

            {fieldCount === 0 && <p className="fb-hint">Click “Add new field” to start building your form.</p>}
          </div>
        )}
      </div>
    </div>
    </Layout>
  );
};

export default FormBuilder;
